import {colors} from "@c10h/colors";
import {useTranslation} from "ni18n";
import React, {useState} from "react";
import {useDispatch} from "react-redux";
import {useFaqIndex} from "src/hooks/useFaqIndex";

import {GA, addToSalesforce} from "../_services/api";
import {c4BLinkedInConversionId} from "../components/_common/_constants";
import {trackToPatient, useEffectAsync} from "../components/_common/Carbon";
import Completed from "../components/v5/CovidReady3/Completed";
import Header from "../components/v5/CovidReady3/Header";
import Hero from "../components/v5/CovidReady3/Hero";
import How from "../components/v5/CovidReady3/How";
import Partners from "../components/v5/CovidReady3/Partners";
import Signup from "../components/v5/CovidReady3/Signup";
import Faq from "../components/v5/Faq/Faq";
import Layout from "../components/v5/Layout";
import ServicesTimedCarousel, {ServicesPropsType} from "../components/v5/ServicesTimedCarousel";
import {actions, useTypedSelector} from "../store";
import {transparent} from "../utils/transparent";

const getStartedTracker = () => {
  GA({
    category: "Carbon Ready",
    action: "Self Serve Lead Form",
    label: "Start Registration",
  });
};

const CovidReady = () => {
  const i18n = useTranslation();

  const {csrfToken} = useTypedSelector(({config}) => config);

  const industryList = [
    "Aerospace & Defense",
    "Agriculture",
    "Apparel",
    "Banking",
    "Biotechnology",
    "Business Services",
    "Communications",
    "Construction",
    "Consulting",
    "Delivery & Logistics",
    "Education",
    "Electronics",
    "Energy",
    "Engineering",
    "Entertainment & Media",
    "Environmental",
    "Film",
    "Financial Services",
    "Food & Beverage",
    "Government",
    "Healthcare",
    "Hospitality",
    "Insurance",
    "Machinery",
    "Manufacturing",
    "Medical Device",
    "Not For Profit",
    "Other",
    "Pharmaceutical",
    "Public Services",
    "Real Estate",
    "Recreation",
    "Retail",
    "Shipping",
    "Technology",
    "Telecommunications",
    "Transportation",
    "Travel",
    "Utilities",
  ];

  const faq = useFaqIndex([
    "How does COVID Ready work?",
    "How do employees onboard through the Carbon Health app?",
    "Which test is used to test employees for COVID-19?",
    "Can employers see an employee's private medical information?",
    "What employee information is being tracked?",
    "Is employee data protected?",
    "Can employees control what data is shared?",
    "How does Carbon perform Employee Contact Tracing?",
    "How does Carbon do Workplace Contact Tracing?",
    "What protections does Carbon have in place to protect my personal and health information from hackers or outsiders getting into the app or into their databases that store my information?",
    "Where do they store this information?",
    "Is there any user agreement, consent forms, or any document that I can review before I even download the app and provide my personal and health information to another company?",
    "How long does Carbon keep personal/health data for?",
    "Is there anything that describes what they are doing, will do, and are allowed to do with my information?",
    "Can I delete the app AND my personal and health information and history if necessary?",
    "How long are we expected to be monitored and provide info to this app?",
  ]);

  const servicesProps: ServicesPropsType = {
    noBtns: true,
    header: i18n.t("COVID-19 Testing"),
    reverse: true,
    customSlides: [
      {
        head: i18n.t("Onsite Testing"),
        text: i18n.t(
          "Enjoy the convenience of high volume testing events at your work location. Carbon Health will coordinate with your team(s) to conduct onsite COVID-19 testing of all employees. Results are tracked and monitored in a customized employer dashboard.",
        ),
        img: "covidReady3/services0",
        color: "#59b3ca",
        btn2: {
          title: i18n.t("Contact Our Team"),
          color: colors.gray800,
          bgColor: transparent(colors.gray800, 5),
          url: "#signup",
        },
      },
      {
        head: i18n.t("In-Clinic Testing"),
        text: i18n.t(
          "Pre schedule appointments at Carbon Health clinics for quick turnaround times and shorter wait periods. Ideal for small numbers of employees needing regular scheduled testing to return to work.",
        ),
        img: "covidReady3/services1",
        color: "#59b3ca",
        btn2: {
          title: i18n.t("Contact Our Team"),
          color: colors.gray800,
          bgColor: transparent(colors.gray800, 5),
          url: "#signup",
        },
      },
      {
        head: i18n.t("At-Home Testing"),
        text: i18n.t(
          "Opt to have your employees test from the comfort and safety of their own homes. Perfect for companies operating in multiple locations with distributed teams.",
        ),
        img: "covidReady3/services2",
        color: "#59b3ca",
        btn2: {
          title: i18n.t("Contact Our Team"),
          color: colors.gray800,
          bgColor: transparent(colors.gray800, 5),
          url: "#signup",
        },
      },
    ],
    innerStyle: {
      paddingTop: "7rem",
    },
  };

  const dispatch = useDispatch();

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [linkedinConversionId, setLinkedinConversionId] = useState(null);
  const [error, setError]: any = useState(false);

  // @ts-expect-error TS7006, TS7006: Parameter 'e' implicitly has an 'any' type.,  Parameter 'captcha' implicitly has an 'any' type.
  const handleSubmit = async (e, captcha) => {
    setSubmitting(true);

    const FirstName = e.target[0].value;
    const LastName = e.target[1].value;
    const Company = e.target[2].value;
    const Email = e.target[3].value;
    const Phone = e.target[4].value;

    addToSalesforce(csrfToken, {
      FirstName,
      LastName,
      Company,
      Email,
      Phone,
      LeadSource: "web",
      captcha,
    })
      .then(() => {
        trackToPatient({
          ga: {
            category: "Carbon Ready",
            action: "Enterprise Lead Form",
            label: Company,
          },
        });
        // @ts-expect-error TS2345: Argument of type '2747490' is not assignable to parameter of type 'SetStateAction<null>'.
        setLinkedinConversionId(c4BLinkedInConversionId);
        setSubmitted(true);
        setSubmitting(false);
      })
      .catch(() => {
        setSubmitted(false);
        setSubmitting(false);
        return setError(true);
      });
  };

  useEffectAsync(async () => {
    dispatch(
      actions.setConfig({
        pageLoaded: true,
      }),
    );
  }, []);

  return (
    <Layout
      pageTitle={i18n.t("COVID Ready: Keep your employees safe and healthy")}
      pageDescription={i18n.t(
        "Carbon Health offers COVID-19 testing, tracking, vaccines, and more.",
      )}
      noHeader
      pageImage="covid-ready"
    >
      {linkedinConversionId && (
        <img
          height="1"
          width="1"
          style={{display: "none"}}
          alt=""
          src={`https://px.ads.linkedin.com/collect/?pid=2265524&conversionId=${linkedinConversionId}&fmt=gif`}
        />
      )}
      <Header {...{getStartedTracker}} />
      <Hero />
      <Partners />
      <How />
      <Completed />
      <ServicesTimedCarousel {...servicesProps} />
      <Signup
        {...{
          csrfToken,
          getStartedTracker,
          handleSubmit,
          submitted,
          submitting,
          industryList,
          error,
        }}
      />
      <Faq faq={faq} />
    </Layout>
  );
};

export default CovidReady;
